import { Box, ClickAwayListener, IconButton, TextField } from "@mui/material";
import {
    ClearIcon,
    DesktopDateTimePicker,
    LocalizationProvider,
    MobileDateTimePicker
} from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import TooltipWrapper from "./TooltipWrapper";

import { isDesktop } from "react-device-detect";
import { useTranslation } from "react-i18next";
import { ColorScheme } from "../../Themes/ColorScheme";

let style = {
	// saved from local component level styling to here⁄
	display: "flex",
	flexDirection: "row",
	alignItems: "center",
	borderRadius: "4px",
	width: "100%",
	height: "100%",
	minHeight: "75px",
	maxHeight: "75px",
	backgroundColor: ColorScheme.lightGrey,
	marginBottom: "10px", // todo fix this, remove (10px) and use gap property in parent
	alignSelf: "center",
	boxSizing: "border-box",
	border: "1px solid " + ColorScheme.darkGrey + ColorScheme.opacity25,
	overflow: "hidden",
	icon: {
		height: "40px",
		minHeight: "40px",
		maxHeight: "40px",
		width: "40px",
		minWidth: "40px",
		maxWidth: "40px",
		display: "flex",
		alignItems: "center",
		justifyContent: "center",
		boxSizing: "border-box",
		marginLeft: "20px",
		cursor: "pointer"
	},
	inputHeader: {
		// not in use
		fontSize: "12px",
		color: ColorScheme.primary,
		fontWeight: "700",
		marginTop: 0
	},

	select: {
		// not in use
		minHeight: "50px",
		backgroundColor: ColorScheme.lightGrey,
		height: undefined,
		"& .MuiOutlinedInput-root": {
			position: "relative",
			// top: "7px",
			backgroundColor: "inherit"
		},

		"fieldset.MuiOutlinedInput-notchedOutline": {
			border: "none"
		},
		"& .MuiFormLabel-root": {
			top: "0px",
			transition: "all 0.2s ease-in-out" // slower animation transition
		},
		"&.Mui-focused": {
			color: ColorScheme.black,
			top: "2px"
		},
		"& .MuiInputLabel-root.Mui-focused": {
			/* color: ColorScheme.black, */
			top: "10px"
		},
		"& .Mui-active": {
			color: ColorScheme.black
		},
		"& .MuiSelect-icon": {
			/* top: "5px", */
			transition: "all 0.2s ease-in-out" // slower animation transition
		}
	},

	pickers: {
		width: "100%",
		height: "100%",
		backgroundColor: ColorScheme.lightGrey,
		minHeight: "75px",
		"& .MuiInputBase-root": {
			// should cover TextField with select property
			minHeight: "55px",
			backgroundColor: ColorScheme.lightGrey,
			"fieldset.MuiOutlinedInput-notchedOutline": {
				border: "none"
			}
		},
		"& .MuiOutlinedInput-root": {
			minHeight: "75px",
			backgroundColor: "inherit",
			position: "relative",
			top: "7px"
		},
		"& .MuiOutlinedInput-notchedOutline": {
			border: "none"
		},
		fieldset: {
			border: "none"
		},
		label: {
			fontSize: "14px",
			top: "28px"
		}
	},

	"& .MuiInputBaseRoot": {
		// should cover TextField with select property
		minHeight: "55px",
		backgroundColor: ColorScheme.lightGrey,
		"fieldset.MuiOutlinedInput-notchedOutline": {
			border: "none"
		}
	},
	"& .MuiOutlinedInput-notchedOutline": {
		border: 0
	}
};


export const TextfieldPicker = ({
    removeIcon,
    isDisabled,
    helpText,
    hasProps,
    fullDate = true,
    value,
    limitMax,
    limitMin,
    excludePast = true,
    valueChange,
    label,
    noTime,
    hideClearButton = false,
    leanToEndOfDay = false,
    maskUnlimitedEnd = false, // If true, only dd:mm:yyyy will be shown if date is > 2038-01-01
    maskUnlimitedStart = false, // If true, only dd:mm:yyyy will be shown if date is in the past
}) => {
    const { t } = useTranslation();

    const [open, setOpen] = useState(false);
    const [warning, setWarning] = useState(false);
    const [justClosed, setJustClosed] = useState(false);

	const openToView = fullDate ? "year" : noTime ? "day" : "hours";

	const maxDate = new Date("2038-01-01");

    const maxValue = limitMax ? new Date(limitMax) : maxDate;
    const minValue = limitMin ? new Date(limitMin) : null;

    const renderViews = fullDate
        ? ["year", "month", "day", "hours", "minutes"]
        : noTime
            ? ["year", "month", "day"]
            : ["hours", "minutes"];

    const handleAccept = (value) => {
        let newValue = dayjs(value);
        if (!fullDate && noTime) {
            newValue = leanToEndOfDay ? newValue.endOf('day') : newValue.startOf('day');
        }
        valueChange(newValue);
        setJustClosed(true);
    };

    const handleChange = (value) => {
        const dateObject = dayjs(value).second(0);

        if (dateObject.isValid()) {
            setWarning(false);
            valueChange(dateObject);
        } else {
            setWarning(true);
        }
    };

    const handleDialogOpen = () => {
        if (!open && !justClosed) setOpen(true);
    };


    useEffect(() => {
        if (justClosed) {
            setTimeout(() => {
                setJustClosed(false);
            }, 500);
        }
    }, [justClosed]);

    return (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
            <ClickAwayListener onClickAway={() => setOpen(false)} mouseEvent="onMouseDown">
                <Box
                    style={{
                        ...style,
                        borderColor: ColorScheme.darkGrey + ColorScheme.opacity25,
                        cursor: "pointer"
                    }}
                    onClick={handleDialogOpen}
                >
                    {!removeIcon && (
                        <Box sx={style.icon}
                        onClick={handleDialogOpen}>
                            <TooltipWrapper title={helpText}>{hasProps}</TooltipWrapper>
                        </Box>
                    )}

                    {isDesktop ? (
                        <DesktopDateTimePicker
                            ampm={false}
                            open={open}
                            autoOk={true}
                            closeOnSelect
                            onClose={() => setOpen(false)}
                            onChange={handleChange}
                            onAccept={handleAccept}
                            clearable
                            slots={{
                                openPickerIcon: () => null,
                                openPickerButton: () => null,
                                TextField: TextField
                            }}
                            slotProps={{
                                tabs: {
                                    hidden: fullDate ? false : true
                                },
                                textField: {
                                    format: fullDate ? "DD.MM.YYYY HH:mm" : noTime ? "DD.MM.YYYY" : "HH:mm",
                                    error: warning,
                                    disableIgnoringDatePartForTimeValidation: !fullDate && !noTime,
                                    InputLabelProps: { ...style.pickers, shrink: true }, // <-- add shrink: true
                                    sx: style.pickers,
                                },
                                actionBar: {
                                    isTime: !fullDate && !noTime,
                                    hidden: false
                                }
                            }}
                            localeText={{
                                toolbarTitle: fullDate
                                    ? t("time.input_date_time")
                                    : noTime
                                        ? t("time.input_date")
                                        : t("time.input_time")
                            }}
                            disablePast={excludePast}
                            openTo={openToView}
                            label={label}
                            views={renderViews}
                            showToolbar
                            disabled={isDisabled}
                            minutesStep={1}
                            value={
                                (dayjs(value).year() >= 2038 && maskUnlimitedEnd)
                                || (dayjs(value).isBefore(new Date()) && maskUnlimitedStart)
                                    ? null
                                    : dayjs(value)
                            }
                            maxDateTime={dayjs(maxDate)}
                            minDateTime={dayjs(minValue)}
                            format={fullDate ? "DD - MM - YYYY HH:mm" : "HH:mm"}
                        />
                    ) : (
                        <MobileDateTimePicker
                            ampm={false}
                            open={open}
                            autoOk={true}
                            closeOnSelect
                            onClose={() => setOpen(false)}
                            onChange={handleChange}
                            onAccept={handleAccept}
                            clearable
                            value={
                                (dayjs(value).year() >= 2038 && maskUnlimitedEnd)
                                || (dayjs(value).isBefore(new Date()) && maskUnlimitedStart)
                                    ? null
                                    : dayjs(value)
                            }
                            disabled={isDisabled}
                            disablePast={excludePast}
                            label={label}
                            openTo={openToView}
                            views={renderViews}
                            format={fullDate ? "DD - MM - YYYY HH:mm" : "HH:mm"}
                            localeText={{
                                toolbarTitle: fullDate
                                    ? t("time.input_date_time")
                                    : noTime
                                        ? t("time.input_date")
                                        : t("time.input_time")
                            }}
                            minDateTime={dayjs(minValue)}
                            maxDateTime={dayjs(maxValue)}
                            minutesStep={1}
                            showToolbar
                            slotProps={{
                                tabs: {
                                    hidden: fullDate ? false : true
                                },
                                textField: {
                                    format: fullDate ? "DD.MM.YYYY HH:mm" : noTime ? "DD.MM.YYYY" : "HH:mm",
                                    error: warning,
                                    disableIgnoringDatePartForTimeValidation: !fullDate && !noTime,
                                    InputLabelProps: { ...style.pickers, shrink: true }, // <-- add shrink: true
                                    sx: style.pickers,
                                },
                                actionBar: {
                                    isTime: !fullDate && !noTime,
                                    hidden: false
                                }
                            }}
                        />
                    )}
                    {!hideClearButton && (
                        <IconButton onClick={e => {
                            e.stopPropagation();
                            valueChange(null)
                        }}>
                            <ClearIcon />
                        </IconButton>
                    )}
                </Box>
            </ClickAwayListener>
        </LocalizationProvider>
    );
};